import type { ReactNode } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import SearchInput from "@/components/shared/SearchInput";
import FormSelect from "@/components/shared/FormSelect";
import type { DataTableFilterConfig, DataTableSort } from "@/components/shared/types/data-table";

interface DataTableToolbarProps {
  title?: string;
  search?: string;
  searchPlaceholder?: string;
  onSearchChange?: (value: string) => void;
  filters?: DataTableFilterConfig[];
  filterValues?: Record<string, string>;
  onFilterChange?: (id: string, value: string) => void;
  toolbarExtra?: ReactNode;
}

const DataTableToolbar = ({
  title,
  search,
  searchPlaceholder,
  onSearchChange,
  filters = [],
  filterValues = {},
  onFilterChange,
  toolbarExtra,
}: DataTableToolbarProps) => {
  const showToolbar = title || onSearchChange || filters.length > 0 || toolbarExtra;

  if (!showToolbar) return null;

  return (
    <CardHeader className="pb-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        {title && <CardTitle className="text-lg">{title}</CardTitle>}
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
          {onSearchChange && (
            <SearchInput
              value={search ?? ""}
              onChange={onSearchChange}
              placeholder={searchPlaceholder}
            />
          )}
          {filters.map((filter) => (
            <FormSelect
              key={filter.id}
              value={filterValues[filter.id] ?? "all"}
              onValueChange={(value) => onFilterChange?.(filter.id, value)}
              options={filter.options}
              placeholder={filter.placeholder ?? filter.label ?? "Filter"}
              triggerClassName="w-full sm:w-[140px]"
            />
          ))}
          {toolbarExtra}
        </div>
      </div>
    </CardHeader>
  );
};

export function SortIcon({ sort, columnId }: { sort?: DataTableSort; columnId: string }) {
  if (sort?.id !== columnId) return <ArrowUpDown className="ml-1 h-3.5 w-3.5 text-muted-foreground/50" />;
  return sort.direction === "asc" ? (
    <ArrowUp className="ml-1 h-3.5 w-3.5 text-primary" />
  ) : (
    <ArrowDown className="ml-1 h-3.5 w-3.5 text-primary" />
  );
}

export default DataTableToolbar;
